import { DialogContent, Dialog as MuiDialog } from '@mui/material'
import { tss } from 'tss-react'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { ModalProps } from './types'
import {
  getSearchModalBasePrimitives,
  modalActionsPrimitives,
  modalTitlePrimitives,
  modalTitleTextPrimitives,
  searchModalContentPrimitives,
} from './variants/primitives'

/**
 * Renders a modal with a search bar for global search
 */
export function SearchModal({
  open,
  onClose,
  children,
  className,
  fullWidth = false,
  showContentBorder = false,
  openedByKeyboard = false,
  ...rest
}: Omit<ModalProps, 'title'> & { openedByKeyboard?: boolean }) {
  const { isMobile } = useDeviceType()
  const { classes, cx } = useStyles({ fullWidth, showContentBorder, openedByKeyboard, isMobile })
  return (
    <MuiDialog
      className={cx(classes.root, className)}
      classes={{
        paper: classes.paper,
        scrollPaper: classes.scrollPaper,
      }}
      disableEscapeKeyDown={false}
      disableRestoreFocus
      fullWidth={fullWidth}
      hideBackdrop={!isMobile}
      onClose={onClose}
      open={open}
      {...rest}
    >
      {children && <DialogContent className={cx(classes.content)}>{children}</DialogContent>}
    </MuiDialog>
  )
}

const useStyles = tss
  .withParams<{
    fullWidth: boolean
    showContentBorder: boolean
    openedByKeyboard: boolean
    isMobile: boolean
  }>()
  .create(({ fullWidth, showContentBorder, openedByKeyboard, isMobile }) => {
    const mobilePaper = {
      borderRadius: '12px 12px 0 0',
      position: 'fixed' as const,
      bottom: 0,
      left: 0,
      right: 0,
      top: '10%',
      margin: 0,
    }

    const mobileScrollPaper = {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
    }

    return {
      actions: modalActionsPrimitives,
      title: modalTitlePrimitives,
      root: {
        ...getSearchModalBasePrimitives(fullWidth),
        zIndex: 1405,
      },
      paper: isMobile
        ? mobilePaper
        : {
            borderRadius: '4px',
            ...(openedByKeyboard
              ? {}
              : {
                  position: 'absolute' as const,
                  top: '1rem',
                  insetInlineStart: '1rem',
                  insetInlineEnd: '0',
                  bottom: '0',
                }),
          },
      scrollPaper: (() => {
        if (isMobile) {
          return mobileScrollPaper
        }
        if (openedByKeyboard) {
          return {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: '-60px',
          }
        }
        return {
          position: 'relative' as const,
          display: 'flex',
          alignItems: 'flex-start !important',
          justifyContent: 'flex-start !important',
          height: '100%',
        }
      })(),
      content: searchModalContentPrimitives(showContentBorder),
      titleText: modalTitleTextPrimitives,
    }
  })
